import { useRef } from 'react'
import ChatInputBox from '@/agent-v2/components/ChatInputBox'
import { DictationSurface } from '@/analytics/enums/dictation.enums'
import ChatWindowUserFilesUpload, { ChatWindowUserFilesUploadRef } from '@/chat-common/components/ChatWindowUserFilesUpload'
import { useChatFileDropzone } from '@/chat-common/hooks/useChatFileDropzone'
import { DropFilesToUploadLabel } from '@/uppy-file-upload/components/DropFilesToUploadLabel'
import { FileUploadContext } from '@/context/file-upload-context'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { Button } from '@/components/button/Button'
import { useMatterOverviewChatComposer } from '@/matter/components/matter-overview/useMatterOverviewChatComposer'

type MatterOverviewChatInputProps = {
  matterId: string
}

export function MatterOverviewChatInput({ matterId }: MatterOverviewChatInputProps) {
  const {
    composerConversationId,
    composerNotReady,
    hasListError,
    hasCreateError,
    hasAttachedSkills,
    hasUnreadyUserFiles,
    inputText,
    matterSubmitBlockedReason,
    onInputTextChange,
    onSubmit,
    retryListLoad,
    retryCreate,
  } = useMatterOverviewChatComposer(matterId)

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const runControlButtonRef = useRef<HTMLButtonElement | null>(null)
  const chatWindowUserFilesUploadRef = useRef<ChatWindowUserFilesUploadRef | null>(null)
  const chatWindowWritingSampleUploadRef = useRef<ChatWindowUserFilesUploadRef | null>(null)

  const { isDraggingFilesOver, dragHandlers: chatFileDragHandlers } = useChatFileDropzone({
    disabled: composerNotReady,
    onFilesDropped: (files) => chatWindowUserFilesUploadRef.current?.addFiles(files),
  })

  // Dictation appends to whatever is already typed, matching the chat composer.
  const appendVoiceTranscript = (transcript: string) => {
    onInputTextChange(inputText.trim() ? `${inputText.trimEnd()} ${transcript}` : transcript)
  }

  if (hasListError) {
    return (
      <div className="mt-3 flex flex-col items-center gap-3 rounded-lg border border-brand-neutral-200 bg-white p-6">
        <InlineMessage variant="error">Unable to load chats for this matter.</InlineMessage>
        <Button type="button" variant="outlined" color="primary" size="sm" onClick={retryListLoad}>
          Retry
        </Button>
      </div>
    )
  }

  if (hasCreateError) {
    return (
      <div className="mt-3 flex flex-col items-center gap-3 rounded-lg border border-brand-neutral-200 bg-white p-6">
        <InlineMessage variant="error">Unable to start a new chat.</InlineMessage>
        <Button type="button" variant="outlined" color="primary" size="sm" onClick={() => void retryCreate()}>
          Retry
        </Button>
      </div>
    )
  }

  const conversationId = composerConversationId ?? ''

  return (
    <div className="relative mt-3" {...chatFileDragHandlers}>
      <ChatWindowUserFilesUpload ref={chatWindowUserFilesUploadRef} conversationId={conversationId} matterId={matterId} />
      <ChatWindowUserFilesUpload ref={chatWindowWritingSampleUploadRef} conversationId={conversationId} matterId={matterId} uploadTarget="writing-sample" />
      <FileUploadContext.Provider
        value={{
          openSystemUserFilesFileSelector: () => chatWindowUserFilesUploadRef.current?.openSystemFileSelector(),
          openSystemWritingSampleFileSelector: () => chatWindowWritingSampleUploadRef.current?.openSystemFileSelector(),
          cancelUserFileUpload: (clientTmpId: string) => {
            chatWindowUserFilesUploadRef.current?.cancelUpload(clientTmpId)
            chatWindowWritingSampleUploadRef.current?.cancelUpload(clientTmpId)
          },
          userFilesUploadDisabled: false,
        }}
      >
        <ChatInputBox
          conversationId={conversationId}
          conversationSubmitBlockedReason={null}
          disableActions={composerNotReady}
          disableInput={composerNotReady}
          errorMessage={null}
          hasAttachedSkills={hasAttachedSkills}
          hasUnreadyUserFiles={hasUnreadyUserFiles}
          inputText={inputText}
          isRecovering={false}
          isRunning={false}
          isStopping={false}
          matterId={matterId}
          matterSubmitBlockedReason={matterSubmitBlockedReason}
          onInputTextChange={onInputTextChange}
          onStop={() => {}}
          onSubmit={onSubmit}
          dictationSurface={DictationSurface.matterOverview}
          onVoiceTranscription={appendVoiceTranscript}
          placeholder="What do you want to work on?"
          recoveryStartedAfterDetachment={false}
          skillsMenuSide="bottom"
          runControlButtonRef={runControlButtonRef}
          textAreaRef={textAreaRef}
        />
      </FileUploadContext.Provider>

      {isDraggingFilesOver && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-[6px] border-[1.5px] border-dashed border-brand-400 bg-white/90">
          <div className="rounded-full border border-brand-200 bg-brand-50 px-4 py-3">
            <DropFilesToUploadLabel />
          </div>
        </div>
      )}
    </div>
  )
}
